import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Button,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { useIdToken } from "@sans-service-tier/sdk-react";
import { useNavigate } from "react-router";
import { clearAuthCookies } from "../util/auth";

interface HeaderProps {
  userInfo?: any;
  adapter?: any;
}

export default function Header({ userInfo, adapter }: HeaderProps) {
  const { loading, token } = useIdToken();
  const navigate = useNavigate();

  // tiny helper to broadcast auth changes app-wide
  const announce = (type: "login" | "logout") => {
    window.dispatchEvent(new CustomEvent("sans:auth", { detail: { type, ts: Date.now() } }));
  };

  // Write id/access cookies used by Lambda@Edge
  useEffect(() => {
    if (token) {
      const authToken = localStorage["sans-auth-id"] ?? "";
      const accessToken = localStorage["sans-auth-access"] ?? "";
      const ATTRS = `Path=/; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
      document.cookie = `id=${authToken}; ${ATTRS}`;
      document.cookie = `access_id=${accessToken}; ${ATTRS}`;

      // Tells CoursePage we are auth'd and can render...
      sessionStorage.removeItem("sans:loginPending");

      // Optional: let listeners know we’re authenticated now
      announce("login");
    }
  }, [token]);

  // Menu state
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(menuEl);
  const givenName = useMemo(() => userInfo?.given_name || userInfo?.name || "", [userInfo]);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleMenuClose = () => setMenuEl(null);

  const goLanding = () => navigate("/"); // basename=/workbook → /workbook

  const doLogout = () => {
    handleMenuClose();
    // mark that we’re leaving; the next page load can read this
    sessionStorage.setItem("sans:logoutPending", "1");
    clearAuthCookies();
    adapter?.logout?.(); // hand off to IdP
  };

  const doLogin = () => {
    sessionStorage.setItem("sans:loginPending", "1");
    window.dispatchEvent(new Event("sans:login-start")); // make CoursePage flip instantly
    adapter?.login?.();
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "#dce6f1",
          color: "inherit",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ minHeight: 56, display: "flex", alignItems: "center", gap: 1 }}>
          {/* Left: SANS Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <img
              src={"/workbook/sans-black.png"}
              alt="SANS Logo"
              style={{ height: 28, width: "auto", display: "block", cursor: "pointer" }}
              onClick={goLanding}
            />
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Right side */}
          {loading ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Loading…
            </Typography>
          ) : userInfo?.sub ? (
            <>
              {/* Account menu */}
              <Tooltip title="Account">
                <IconButton
                  onClick={handleMenuOpen}
                  size="small"
                  aria-label="User menu"
                  aria-controls={openMenu ? "user-menu" : undefined}
                  aria-haspopup="menu"
                  aria-expanded={openMenu ? "true" : undefined}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#004f77",
                    "&:hover": { bgcolor: "#0a6a9e" },
                  }}
                >
                  <AccountCircleIcon htmlColor="#fff" />
                </IconButton>
              </Tooltip>

              <Menu
                id="user-menu"
                anchorEl={menuEl}
                open={openMenu}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { elevation: 3 } }}
              >
                <MenuItem disabled sx={{ opacity: 0.75 }}>
                  <ListItemIcon>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  Signed in as {givenName || "User"}
                </MenuItem>

                <Divider />

                <MenuItem onClick={doLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={doLogin}
              sx={{
                bgcolor: "#004f77",
                "&:hover": { bgcolor: "#0a6a9e" },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
}
